import React from 'react';
import type { DisplayLine, MatrixTableData } from '@app-types/performanceTrends';
import './FinancialMatrixTable.less';

export interface FinancialMatrixTableProps {
  data: MatrixTableData;
  /** Accessible caption (visually hidden). */
  caption?: string;
}

function renderLines(lines: DisplayLine[]): React.ReactNode {
  return lines.map((line, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <span key={i} className={`trends-matrix__line trends-matrix__line--${line.tone ?? 'default'}`}>
      {line.text}
    </span>
  ));
}

/**
 * Generic semantic matrix table for the Performance Trends screen.
 * Ports the Summary `progress-status__matrix-*` visual pattern into
 * Trends-specific classes so the Summary CSS is untouched (see PT02).
 * Wide tables scroll horizontally via the wrapper.
 */
const FinancialMatrixTable: React.FC<FinancialMatrixTableProps> = ({ data, caption }) => {
  const { columns, rows, minWidth, labelColumnWidth, nodeId } = data;

  return (
    <div className="trends-table-wrap">
      <table
        className="trends-matrix-table"
        style={minWidth ? { minWidth } : undefined}
        data-node-id={nodeId}
      >
        {caption && <caption className="trends-matrix__caption">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col, index) => {
              const headerWidth =
                index === 0 ? labelColumnWidth : col.width;
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={`trends-matrix__cell trends-matrix__cell--header trends-matrix__cell--${
                    col.align ?? 'right'
                  }`}
                  style={headerWidth ? { width: headerWidth } : undefined}
                >
                  {renderLines(col.label)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-node-id={row.nodeId}>
              <th
                scope="row"
                className="trends-matrix__cell trends-matrix__cell--header trends-matrix__cell--left"
                style={labelColumnWidth ? { width: labelColumnWidth } : undefined}
              >
                {renderLines(row.label)}
              </th>
              {row.cells.map((cellLines, index) => (
                <td
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={`trends-matrix__cell trends-matrix__cell--data trends-matrix__cell--${
                    columns[index + 1]?.align ?? 'right'
                  }`}
                >
                  {renderLines(cellLines)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FinancialMatrixTable;
